import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

function RecursiveInjectMenu({ pathPrefix, targetSchema, targetTable, token, injectedColumns, toggleInjectedColumn, depth = 1, apiPrefix }) {
    const [expandedCols, setExpandedCols] = useState({});
    const [columns, setColumns] = useState([]);
    const [fks, setFks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const schemaPrefix = apiPrefix && apiPrefix.startsWith('/api/remote-sql') ? apiPrefix : '/api/data';
                const prefix = `${schemaPrefix}/schema`;
                const [colRes, fkRes] = await Promise.all([
                    fetch(`${prefix}/metadata/${targetSchema}/${targetTable}`, { headers: { 'Authorization': `Bearer ${token}` } }),
                    fetch(`${prefix}/fks`, { headers: { 'Authorization': `Bearer ${token}` } })
                ]);
                if (colRes.ok && fkRes.ok) {
                    setColumns(await colRes.json());
                    const allFks = await fkRes.json();
                    setFks(Array.isArray(allFks) ? allFks.filter(f => f.TableName === `${targetSchema}.${targetTable}`) : []);
                }
            } catch(e) {
                console.error("Failed to load schema for injection:", e);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [targetSchema, targetTable, token]);

    if (loading) return <div className="px-2 py-1 text-xs text-slate-500">Loading...</div>;

    return (
        <div className={`space-y-0.5 ${depth > 1 ? 'pl-2 ml-2 border-l border-slate-700/50' : ''}`}>
            {columns.map(col => {
                const colPath = `${pathPrefix}->${col.ColumnName}`;
                const isAdded = injectedColumns.includes(colPath);
                const fk = fks.find(f => f.ColumnName === col.ColumnName);

                return (
                    <div key={col.ColumnName} className="flex flex-col">
                        <label className="flex items-center gap-2 px-2 py-1 hover:bg-slate-700 rounded cursor-pointer group">
                            <input 
                                type="checkbox" 
                                checked={isAdded}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    toggleInjectedColumn(colPath);
                                }}
                                className="rounded bg-slate-900 border-slate-600 focus:ring-0 focus:ring-offset-0"
                            />
                            <span className="text-sm text-slate-200 truncate flex-1">{col.ColumnName}</span>
                            {col.IsPrimaryKey && <span className="text-[10px] text-yellow-500">PK</span>}
                            {fk && depth < 20 && (
                                <button 
                                    className="text-[10px] text-blue-300 hover:text-white bg-slate-800 border border-slate-600 px-1.5 py-0.5 rounded transition-colors opacity-50 group-hover:opacity-100"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpandedCols(prev => ({...prev, [col.ColumnName]: !prev[col.ColumnName]}));
                                    }}
                                    title={`Expand ${fk.RefTableName}`}
                                >
                                    {expandedCols[col.ColumnName] ? '− Collapse' : '+ Expand'}
                                </button>
                            )}
                        </label>
                        {fk && fk.RefTableName === `${targetSchema}.${targetTable}` && (
                            <label className="flex items-center gap-2 px-2 py-1 hover:bg-slate-700 rounded cursor-pointer group ml-4 border-l border-teal-700/50">
                                <input 
                                    type="checkbox" 
                                    checked={injectedColumns.includes(`${colPath}->[Hierarchy]`)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        toggleInjectedColumn(`${colPath}->[Hierarchy]`);
                                    }}
                                    className="rounded bg-slate-900 border-slate-600 focus:ring-0 focus:ring-offset-0"
                                />
                                <span className="text-sm text-teal-300 truncate flex-1 italic">[Hierarchy] (Recursive Path)</span>
                                <span className="material-symbols-outlined text-[14px] text-teal-400 opacity-50" title="Virtual Column using Recursive CTE">account_tree</span>
                            </label>
                        )}
                        {fk && expandedCols[col.ColumnName] && depth < 20 && (
                            <RecursiveInjectMenu 
                                pathPrefix={colPath}
                                targetSchema={fk.RefTableName.includes('.') ? fk.RefTableName.split('.')[0] : 'dbo'}
                                targetTable={fk.RefTableName.includes('.') ? fk.RefTableName.split('.')[1] : fk.RefTableName}
                                token={token}
                                injectedColumns={injectedColumns}
                                toggleInjectedColumn={toggleInjectedColumn}
                                depth={depth + 1}
                                apiPrefix={apiPrefix}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function DropdownPill({ fk, injectedColumns, toggleInjectedColumn, token, apiPrefix }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const portalRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    const targetName = fk.RefTableName;
    const [refSchema, refTable] = targetName.includes('.') ? targetName.split('.') : ['dbo', targetName];

    // Check if any column is injected for this relationship
    const isInjected = injectedColumns.some(col => col.startsWith(`${fk.ColumnName}->`));

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isClickInsideTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
            const isClickInsidePortal = portalRef.current && portalRef.current.contains(event.target);
            
            if (!isClickInsideTrigger && !isClickInsidePortal) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 8, left: rect.left });
        setIsOpen(!isOpen);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={handleToggle}
                className={`px-3 py-1 rounded text-xs transition-colors flex items-center gap-1 border ${
                    isInjected 
                        ? 'bg-blue-600 border-blue-500 text-white shadow-inner shadow-blue-900' 
                        : 'bg-slate-800 border-blue-900/50 text-blue-300 hover:bg-slate-700 hover:border-blue-700'
                }`}
                title={`Join ${targetName} via ${fk.ColumnName} (Many-to-One)`}
            >
                <span className="material-symbols-outlined text-[16px]">data_object</span>
                <span>Inject from <strong>{targetName.split('.').pop()}</strong></span>
                <span className="text-slate-400 opacity-50 ml-1 text-[10px]">({fk.ColumnName})</span>
            </button>

            {isOpen && createPortal(
                <div 
                    ref={portalRef}
                    className="fixed w-72 bg-slate-800 border border-slate-600 rounded shadow-xl z-[9999] max-h-96 flex flex-col"
                    style={{ top: dropdownPos.top, left: dropdownPos.left }}
                >
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider p-2 border-b border-slate-700 bg-slate-900/50 sticky top-0 shrink-0">
                        Inject Columns from {targetName}
                    </div>
                    <div className="overflow-y-auto p-1 grow">
                        <RecursiveInjectMenu 
                            pathPrefix={fk.ColumnName}
                            targetSchema={refSchema}
                            targetTable={refTable}
                            token={token}
                            injectedColumns={injectedColumns}
                            toggleInjectedColumn={toggleInjectedColumn}
                            depth={1}
                            apiPrefix={apiPrefix}
                        />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export function ColumnInjector({ 
    targetSchema,
    targetTable,
    fks, 
    reverseFks, 
    isPolymorphicTable, 
    injectedColumns, 
    toggleInjectedColumn, 
    activeSubgrids, 
    setActiveSubgrids,
    token,
    showPolymorphicInjector = true,
    apiPrefix
}) {
    const toggleSubgrid = (subgridKey) => {
        setActiveSubgrids(prev => {
            if (prev.includes(subgridKey)) return prev.filter(k => k !== subgridKey);
            return [...prev, subgridKey];
        });
    };

    return (
        <div className="flex flex-wrap items-center gap-2 bg-slate-900 p-1.5 rounded-md border border-slate-700">
            {/* Forward Relationships (M:1) -> Dropdown to inject columns */}
            {fks && fks.map(fk => (
                <DropdownPill 
                    key={`fwd-${fk.ColumnName}`}
                    fk={fk}
                    injectedColumns={injectedColumns}
                    toggleInjectedColumn={toggleInjectedColumn}
                    token={token}
                    apiPrefix={apiPrefix}
                />
            ))}

            {/* Reverse Relationships (1:M) -> Toggle Subgrid */}
            {reverseFks && reverseFks.map(fk => {
                const sourceName = `${fk.TableName}`; // The table that points to us
                const subgridKey = `${sourceName}:${fk.ColumnName}`;
                const isActive = activeSubgrids.includes(subgridKey);

                return (
                    <button 
                        key={`rev-${subgridKey}`}
                        onClick={() => toggleSubgrid(subgridKey)}
                        className={`px-3 py-1 rounded text-xs transition-colors flex items-center gap-1 border ${
                            isActive 
                                ? 'bg-teal-600 border-teal-500 text-white shadow-inner shadow-teal-900' 
                                : 'bg-slate-800 border-teal-900/50 text-teal-300 hover:bg-slate-700 hover:border-teal-700'
                        }`}
                        title={`Show subgrid of ${sourceName} via ${fk.ColumnName} (One-to-Many)`}
                    >
                        <span className="material-symbols-outlined text-[16px]">{isActive ? 'table_rows_narrow' : 'table_rows'}</span>
                        <span>View <strong>{sourceName.split('.')[1]}</strong> Subgrid</span>
                        <span className="text-slate-400 opacity-50 ml-1 text-[10px]">({fk.ColumnName})</span>
                    </button>
                );
            })}

            {/* Self-Join Subgrid (Group By Mode Helper) */}
            {targetSchema && targetTable && (
                <button 
                    onClick={() => toggleSubgrid(`${targetSchema}.${targetTable}:SELF`)}
                    className={`px-3 py-1 rounded text-xs transition-colors flex items-center gap-1 border ${
                        activeSubgrids.includes(`${targetSchema}.${targetTable}:SELF`)
                            ? 'bg-amber-600 border-amber-500 text-white shadow-inner shadow-amber-900' 
                            : 'bg-slate-800 border-amber-900/50 text-amber-300 hover:bg-slate-700 hover:border-amber-700'
                    }`}
                    title={`Self Subgrid for grouping levels`}
                >
                    <span className="material-symbols-outlined text-[16px]">{activeSubgrids.includes(`${targetSchema}.${targetTable}:SELF`) ? 'account_tree' : 'account_tree'}</span>
                    <span>Self-Subgrid (Grouping)</span>
                </button>
            )}

            {/* Polymorphic Relationships */}
            {isPolymorphicTable && showPolymorphicInjector && (
                <PolymorphicDropdownPill 
                    injectedColumns={injectedColumns}
                    toggleInjectedColumn={toggleInjectedColumn}
                    token={token}
                />
            )}
        </div>
    );
}

function PolymorphicDropdownPill({ injectedColumns, toggleInjectedColumn, token }) {
    const [isOpen, setIsOpen] = useState(false);
    const [targetTableRaw, setTargetTableRaw] = useState('');
    const [targetCols, setTargetCols] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // To identify polymorphic tables we usually rely on TargetEntityType and TargetRecordID
    const polyPrefix = `TargetEntityType:TargetRecordID`;

    const fetchColumns = async () => {
        if (!targetTableRaw || !targetTableRaw.includes('.')) return alert("Please enter a valid Schema.Table (e.g. Network.Devices)");
        setLoading(true);
        try {
            const [schema, table] = targetTableRaw.split('.');
            const res = await fetch(`/api/schema/${schema}/${table}/columns`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) throw new Error("Failed to load schema");
            const data = await res.json();
            setTargetCols(data);
        } catch (e) {
            alert(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`px-3 py-1 rounded text-xs transition-colors flex items-center gap-1 border border-purple-900/50 text-purple-300 hover:bg-slate-700 hover:border-purple-700`}
                title="Inject columns from a specific Polymorphic Target"
            >
                <span className="material-symbols-outlined text-[16px]">device_hub</span>
                <span>Polymorphic Link</span>
            </button>

            {isOpen && (
                <div className="absolute top-full mt-1 left-0 bg-slate-800 border border-slate-600 rounded shadow-xl p-3 z-50 min-w-[250px]">
                    <div className="text-xs text-slate-400 mb-2 font-semibold uppercase">Target Table</div>
                    <div className="flex gap-2 mb-3">
                        <input 
                            type="text" 
                            placeholder="e.g. Network.Devices" 
                            className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded px-2 py-1 w-full"
                            value={targetTableRaw}
                            onChange={e => setTargetTableRaw(e.target.value)}
                        />
                        <button onClick={fetchColumns} disabled={loading} className="bg-purple-600 text-white px-2 rounded hover:bg-purple-500">
                            {loading ? '...' : 'Go'}
                        </button>
                    </div>

                    {targetCols.length > 0 && (
                        <div className="max-h-64 overflow-y-auto pr-1 flex flex-col gap-1">
                            {targetCols.map(col => {
                                const path = `${polyPrefix}->${targetTableRaw}->${col.ColumnName}`;
                                const isActive = injectedColumns.includes(path);
                                return (
                                    <button
                                        key={path}
                                        onClick={() => toggleInjectedColumn(path)}
                                        className={`text-left px-2 py-1 text-xs rounded transition-colors ${isActive ? 'bg-purple-900/50 text-purple-200' : 'text-slate-300 hover:bg-slate-700'}`}
                                    >
                                        <span className="material-symbols-outlined text-[12px] inline align-text-bottom mr-1">
                                            {isActive ? 'check_box' : 'check_box_outline_blank'}
                                        </span>
                                        {col.ColumnName}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
